import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import type { NavLinkScope, SavedNavLink } from '../types';

export const NAV_LINKS_QUERY_KEY = 'nav-links';

function getNavLinksQueryKey(
  tenantId: string | undefined,
  userId: string | undefined,
  scope: NavLinkScope,
) {
  return [NAV_LINKS_QUERY_KEY, tenantId, userId, scope] as const;
}

export function useNavLinks(scope: NavLinkScope, enabled = true) {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const userId = user?.userId;

  return useQuery({
    queryKey: getNavLinksQueryKey(tenantId, userId, scope),
    queryFn: async (): Promise<SavedNavLink[]> => {
      if (!tenantId) return [];

      switch (scope) {
        case 'User':
          return apiClient.getMyNavLinks(tenantId);
        case 'Tenant':
          return apiClient.getTenantNavLinks(tenantId);
        case 'System':
          return apiClient.getSystemNavLinks(tenantId);
        default:
          return [];
      }
    },
    enabled: enabled && !!tenantId && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllNavLinks(enabled = true) {
  const userLinks = useNavLinks('User', enabled);
  const tenantLinks = useNavLinks('Tenant', enabled);
  const systemLinks = useNavLinks('System', enabled);

  return {
    data: [
      ...(userLinks.data ?? []),
      ...(tenantLinks.data ?? []),
      ...(systemLinks.data ?? []),
    ],
    isLoading: userLinks.isLoading || tenantLinks.isLoading || systemLinks.isLoading,
    isError: userLinks.isError || tenantLinks.isError || systemLinks.isError,
  };
}

export function useInvalidateNavLinks() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const userId = user?.userId;

  return (scope?: NavLinkScope) => {
    if (scope && tenantId && userId) {
      queryClient.invalidateQueries({
        queryKey: getNavLinksQueryKey(tenantId, userId, scope),
      });
    }

    queryClient.invalidateQueries({ queryKey: [NAV_LINKS_QUERY_KEY] });
  };
}
